import React from 'react';
import {
    CheckCircleFilled,
    CopyOutlined,
    DeleteOutlined,
    EditOutlined,
    ExclamationCircleOutlined,
    InfoCircleFilled,
} from '@ant-design/icons';
import { notification } from 'antd';
import { colorConstants } from 'config/colorConstants';
import { PricingSimulatorNotificationMessages } from 'utils/Enums';
import { normalizePpaTableRowAction, PpaTableRowAction } from 'types/ppa';

export const getActionIcon = (actionLabel: string) => {
    const action = normalizePpaTableRowAction(actionLabel);
    if (action === PpaTableRowAction.Edit) {
        return <EditOutlined className="text-[16px]" />;
    }
    if (action === PpaTableRowAction.Duplicate) {
        return <CopyOutlined className="text-[16px]" />;
    }
    if (action === PpaTableRowAction.Delete) {
        return <DeleteOutlined className="text-[16px]" />;
    }
    return null;
};

export const scenarioDeleteConfirmIcon = (
    <ExclamationCircleOutlined style={{ color: '#FF4D4F' }} />
);

export const scenarioDeleteSuccessIcon = (
    <InfoCircleFilled style={{ color: colorConstants.purpleFont, fontSize: 24 }} />
);

export const scenarioDuplicateSuccessIcon = (
    <CheckCircleFilled style={{ color: colorConstants.notificationSuccess, fontSize: 24 }} />
);

const SCENARIO_ACTION_SUCCESS_NOTIFICATION_CLASS =
    'scenario-action-success-notification shadow-axesDropdown [&_.ant-notification-notice]:!flex [&_.ant-notification-notice]:!items-start [&_.ant-notification-notice-message]:!m-0 [&_.ant-notification-notice-message]:!mb-0 [&_.ant-notification-notice-message]:!flex-1 [&_.ant-notification-notice-message]:!p-0 [&_.ant-notification-notice-close]:!top-5 [&_.ant-notification-notice-close]:!right-6 [&_.ant-notification-notice-close]:!mt-0 [&_.ant-notification-notice-close]:!translate-y-0';

const SCENARIO_ACTION_SUCCESS_NOTIFICATION_STYLE = {
    padding: '20px 24px',
    borderRadius: 8,
    background: colorConstants.white,
};

type ScenarioActionSuccessToastProps = {
    icon: React.ReactNode;
    message: string;
};

const ScenarioActionSuccessToast = ({ icon, message }: ScenarioActionSuccessToastProps) => (
    <div className="flex w-full items-start gap-3 pr-8">
        <span className="inline-flex shrink-0 items-center">{icon}</span>
        <span
            className="leading-6"
            style={{ color: colorConstants.blackTransparent88, fontWeight: 400, fontSize: 16 }}
        >
            {message}
        </span>
    </div>
);

const showScenarioActionSuccessNotification = (
    icon: React.ReactNode,
    message: string,
    options?: { width?: number | string },
) => {
    notification.open({
        message: <ScenarioActionSuccessToast icon={icon} message={message} />,
        duration: 5,
        placement: 'topRight',
        className: SCENARIO_ACTION_SUCCESS_NOTIFICATION_CLASS,
        style: {
            ...SCENARIO_ACTION_SUCCESS_NOTIFICATION_STYLE,
            width: options?.width ?? 384,
        },
    });
};

export const showScenarioDuplicatedSuccessNotification = () => {
    showScenarioActionSuccessNotification(
        scenarioDuplicateSuccessIcon,
        PricingSimulatorNotificationMessages.DUPLICATE_SCENARIO_SUCCESS,
        { width: 'max-content' },
    );
};

export const showScenarioRenamedSuccessNotification = (scenarioName: string) => {
    notification.success({
        duration: 5,
        icon: scenarioDuplicateSuccessIcon,
        message: PricingSimulatorNotificationMessages.RENAME_SCENARIO_SUCCESS_TITLE,
        description: `"${scenarioName}"`,
        placement: 'topRight',
        style: { width: 350, height: 'auto', borderRadius: 8 },
    });
};

export const showScenarioDeletedSuccessNotification = () => {
    showScenarioActionSuccessNotification(
        scenarioDeleteSuccessIcon,
        PricingSimulatorNotificationMessages.DELETE_SCENARIO_SUCCESS,
        { width: 'max-content' },
    );
};

export const showResetInputsSuccessNotification = () => {
    notification.success({
        duration: 5,
        icon: scenarioDuplicateSuccessIcon,
        message: PricingSimulatorNotificationMessages.RESET_INPUTS_SUCCESS_TITLE,
        description: PricingSimulatorNotificationMessages.RESET_INPUTS_SUCCESS_DESCRIPTION,
        placement: 'topRight',
        style: { width: 384, height: 'auto', borderRadius: 8 },
    });
};
